import { Venue, YearlyRating } from '../services/api';

/**
 * The rating to show for a venue in a given year.
 *
 * Prefers an exact year match and falls back to the venue's most recent
 * rating, so a venue carried over from an earlier guide still shows its
 * stars rather than nothing at all.
 */
export const getYearlyRating = (venue: Venue, year: number): YearlyRating | undefined => {
  const exact = venue.yearly_ratings.find(rating => rating.year === year);
  if (exact) {
    return exact;
  }
  if (venue.yearly_ratings.length === 0) {
    return undefined;
  }
  return [...venue.yearly_ratings].sort((a, b) => b.year - a.year)[0];
};

/** Star count for sorting: venues without stars sort as zero rather than dropping out. */
export const getMichelinStars = (venue: Venue, year: number): number =>
  getYearlyRating(venue, year)?.michelin_stars ?? 0;

export type SortOption = 'default' | 'name' | 'stars';

export const SORT_LABELS: Record<SortOption, string> = {
  default: 'Default order',
  name: 'Name (A-Z)',
  stars: 'Most stars',
};

/** Accent- and case-insensitive, so "Bonheur by Matt Abé" lands where a reader expects. */
const byName = (a: Venue, b: Venue) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Returns a new sorted array - never mutates the input, which is a filtered
 * memo derived from component state.
 *
 * "default" preserves whatever order the API returned. Star sorting falls back
 * to alphabetical within a star level, so equal-starred venues have a stable,
 * readable order instead of the API's arbitrary one.
 */
export const sortVenues = (venues: Venue[], sortBy: SortOption, year: number): Venue[] => {
  switch (sortBy) {
    case 'name':
      return [...venues].sort(byName);
    case 'stars':
      return [...venues].sort(
        (a, b) => getMichelinStars(b, year) - getMichelinStars(a, year) || byName(a, b)
      );
    default:
      return venues;
  }
};
